/**
 * Margin arithmetic and client-side pivoting. PURE — no I/O, no React.
 *
 * The margin figure is the reason this whole design exists (spec §4):
 *   margin = (creditsCharged × creditUnitPriceUsd) − costUsd
 *
 * costUsd and creditsCharged are independent recorded facts. Neither is ever
 * derived from the other here, so a pricing bug in one stays detectable
 * against the other instead of propagating into both (spec §9.3).
 */

import type { AiSpendEventRow, AiSpendReportRow, PivotDimension } from './report';

export interface MarginRow extends AiSpendReportRow {
  tokens: number;
  revenueUsd: number;
  marginUsd: number;
  marginPct: number;
}

export interface PivotTotals {
  requests: number;
  /** Every attempt the vendor billed. Exceeds `requests` when the router
   *  retried — the visible signal that we paid more than once for one call. */
  attempts: number;
  tokens: number;
  costUsd: number;
  creditsCharged: number;
  revenueUsd: number;
  marginUsd: number;
  marginPct: number;
  failedCount: number;
  estimatedCostUsd: number;
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export function computeRevenueUsd(creditsCharged: number, creditUnitPriceUsd: number): number {
  return num(creditsCharged) * num(creditUnitPriceUsd);
}

/**
 * Revenue minus cost. DELIBERATELY unclamped: a negative margin is a real
 * loss (a failed call refunds the tenant but the vendor still billed us —
 * spec M5) and hiding it behind a zero floor would defeat the surface.
 */
export function computeMarginUsd(
  creditsCharged: number,
  creditUnitPriceUsd: number,
  costUsd: number,
): number {
  return computeRevenueUsd(creditsCharged, creditUnitPriceUsd) - num(costUsd);
}

const pct = (marginUsd: number, revenueUsd: number): number =>
  revenueUsd > 0 ? (marginUsd / revenueUsd) * 100 : 0;

export function withMargin(rows: AiSpendReportRow[], creditUnitPriceUsd: number): MarginRow[] {
  return rows.map((r) => {
    const revenueUsd = computeRevenueUsd(r.creditsCharged, creditUnitPriceUsd);
    const marginUsd = revenueUsd - num(r.costUsd);
    return {
      ...r,
      // Cache tokens are billed tokens and belong in the total.
      tokens: num(r.inputTokens) + num(r.outputTokens) + num(r.cacheReadTokens) + num(r.cacheWriteTokens),
      revenueUsd,
      marginUsd,
      marginPct: pct(marginUsd, revenueUsd),
    };
  });
}

export function sumPivot(rows: MarginRow[]): PivotTotals {
  const t = rows.reduce<PivotTotals>(
    (acc, r) => ({
      requests: acc.requests + num(r.requests),
      attempts: acc.attempts + num(r.attempts),
      tokens: acc.tokens + num(r.tokens),
      costUsd: acc.costUsd + num(r.costUsd),
      creditsCharged: acc.creditsCharged + num(r.creditsCharged),
      revenueUsd: acc.revenueUsd + num(r.revenueUsd),
      marginUsd: acc.marginUsd + num(r.marginUsd),
      marginPct: 0,
      failedCount: acc.failedCount + num(r.failedCount),
      estimatedCostUsd: acc.estimatedCostUsd + num(r.estimatedCostUsd),
    }),
    {
      requests: 0, attempts: 0, tokens: 0, costUsd: 0, creditsCharged: 0,
      revenueUsd: 0, marginUsd: 0, marginPct: 0, failedCount: 0, estimatedCostUsd: 0,
    },
  );
  // Computed from the TOTALS. Averaging row percentages would weight a
  // one-request tenant equally with a ten-thousand-request tenant.
  return { ...t, marginPct: pct(t.marginUsd, t.revenueUsd) };
}

function groupOf(e: AiSpendEventRow, dimension: PivotDimension): { key: string; label: string } {
  switch (dimension) {
    case 'tenant':
      return { key: e.tenantId, label: e.tenantName || e.tenantId };
    case 'service':
      return { key: e.service, label: e.service };
    case 'feature':
      return { key: e.feature, label: e.feature };
    case 'role':
      return { key: e.roleId, label: e.roleId };
    case 'model':
    default:
      // Provider-qualified: two vendors can ship a model of the same name and
      // merging them would attribute one vendor's cost to the other.
      return { key: `${e.providerId}#${e.modelId}`, label: `${e.providerId} / ${e.modelId}` };
  }
}

export function pivotEvents(
  events: AiSpendEventRow[],
  dimension: PivotDimension,
): AiSpendReportRow[] {
  const acc = new Map<string, AiSpendReportRow & { _latencySum: number }>();
  for (const e of events) {
    const { key, label } = groupOf(e, dimension);
    const cur = acc.get(key) ?? {
      key, label,
      requests: 0, inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0,
      costUsd: 0, creditsCharged: 0, attempts: 0, failedCount: 0,
      estimatedCostUsd: 0, avgLatencyMs: 0, _latencySum: 0,
    };
    cur.requests += 1;
    cur.inputTokens += num(e.inputTokens);
    cur.outputTokens += num(e.outputTokens);
    cur.cacheReadTokens += num(e.cacheReadTokens);
    cur.cacheWriteTokens += num(e.cacheWriteTokens);
    cur.costUsd += num(e.costUsd);
    cur.creditsCharged += num(e.creditsCharged);
    cur.attempts += num(e.attempts);
    if (e.outcome === 'failed') cur.failedCount += 1;
    if (e.costEstimated) cur.estimatedCostUsd += num(e.costUsd);
    cur._latencySum += num(e.latencyMs);
    acc.set(key, cur);
  }
  return Array.from(acc.values()).map(({ _latencySum, ...r }) => ({
    ...r,
    avgLatencyMs: r.requests > 0 ? Math.round(_latencySum / r.requests) : 0,
  }));
}

export function sortByMargin(rows: MarginRow[], direction: 'asc' | 'desc' = 'desc'): MarginRow[] {
  return [...rows].sort((a, b) =>
    direction === 'asc' ? a.marginUsd - b.marginUsd : b.marginUsd - a.marginUsd,
  );
}
